/** @file mesh.setMirrorVertexMap: vertex correspondence with the -L/-R counterpart mesh, both ways. */
import type { Draft } from 'immer';
import { z } from 'zod';
import type { Doc, MeshAtt } from '@pose2d/format/types';
import { mirrorName } from '@pose2d/format/doc/mirrorName';
import { throwError } from '@pose2d/format/throwError';
import { defineCommand } from '../defineCommand';
import { dropMirrorMaps } from './dropMirrorMaps';
import { invertPermutation } from './invertPermutation';
import { meshDraft } from './meshDraft';
import { mirrorCounterpart } from './mirrorCounterpart';
import { sameNumbers } from './sameNumbers';

/** Writes `map` on `mesh` and its inverse on the counterpart (VALIDATION at `target` without one of equal count); false when both already hold them. */
function pairMaps(draft: Draft<Doc>, mesh: MeshAtt, map: readonly number[]): boolean {
  const pairName = mirrorName(mesh.name);
  const pair = mirrorCounterpart(draft, mesh.name)
    ?? throwError('VALIDATION', undefined, { path: 'target', hint: pairName === null ? `${mesh.name} has no -L/-R suffix` : `no mesh named ${pairName} to pair with ${mesh.name}` });
  const count = mesh.uvs.length / 2;
  const pairCount = pair.mesh.uvs.length / 2;
  if (pairCount !== count) throwError('VALIDATION', undefined, { path: 'target', hint: `${pair.mesh.name} has ${pairCount} vertices and ${mesh.name} ${count}; mirrored meshes need the same count` });
  const inverse = invertPermutation(map, count, 'map');
  if (sameNumbers(mesh.mirrorVertexMap, map) && sameNumbers(pair.mesh.mirrorVertexMap, inverse)) return false;
  pair.mesh.mirrorVertexMap = inverse;
  mesh.mirrorVertexMap = [...map];
  return true;
}

/** mesh.setMirrorVertexMap: map[i] = counterpart vertex mirroring vertex i; the counterpart gets the inverse; null removes both. */
export const command = defineCommand({
  name: 'mesh.setMirrorVertexMap',
  description: 'Set map[i] = the vertex of the -L/-R counterpart mesh that mirrors vertex i (a permutation; both meshes need the same vertex count and the counterpart gets the inverse map); null removes both maps.',
  input: z.object({ target: z.string().min(1), map: z.array(z.number().int().nonnegative()).nullable() }).strict(),
  apply(draft, input, ctx) {
    const { mesh } = meshDraft(draft, ctx, input.target);
    const changed = input.map === null ? dropMirrorMaps(draft, mesh) : pairMaps(draft, mesh, input.map);
    if (changed) draft.meta.updatedAt = ctx.now;
  },
});
